import React, { useState, useRef, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Paper,
  Grid,
  IconButton,
  Chip,
  Divider,
  styled,
  Stack,
  Collapse,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBack,
  CloudUpload,
  Add,
  Close,
  Home,
  Apartment,
  Business,
  House,
  Villa,
  Landscape,
  LocationOn,
  CheckCircle,
  Cancel,
  ExpandLess,
  ExpandMore,
  DeleteOutline,
  Security,
  FitnessCenter,
  SportsBasketball,
  SelfImprovement,
  WorkspacePremium,
  Videocam,
  Pool,
  Spa,
  EmojiEvents,
  DirectionsBike,
  SportsVolleyball,
  LocalLibrary,
  BusinessCenter,
  Restaurant,
  ElectricalServices,
  FormatPaint,
  Kitchen,
  DoorFront,
  SmartToy,
  Hotel,
  Bathroom,
  SportsScore,
  Storefront,
  People,
  Radio,
  Park,
  ChildCare,
  DirectionsRun,
  Forest,
  BeachAccess,
  MyLocation,
  // New icons for expanded features
  Description,
  AccessTime,
  VerifiedUser,
  CropSquare,
  Layers,
  Domain,
  LocationCity,
  DirectionsCar,
  AccountBalance,
  LocalFireDepartment,
  Theaters,
  Celebration,
  LocalCafe,
  SportsTennis,
  SportsCricket,
  SportsSoccer,
  RollerSkating,
  Doorbell,
  Phone,
  ExitToApp,
  Lock,
  Bolt,
  WaterDrop,
  WbSunny,
  EvStation,
  LocalGasStation,
  Elevator,
  SupportAgent,
  Chair,
  Build,
  CleaningServices,
  Deck,
  LocalPharmacy,
  LocalAtm,
  ContentCut,
  LocalLaundryService,
  LocalHospital,
  ChildFriendly,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "./../../AuthContext/AuthContext";
import { BASE_URL, GOOGLEMAPS_API_KEY } from "./../../Api/ApiUrls";
import FullScreenLoader from "../../FullScreenLoader";
import SuccessAlert from "../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert";
import FailureAlert from "../../../Components/Users/Alertpage/FailureAlert/FailureAlert";
import FormsBottomNavbar from "./FormsBottomNavbar";
import SearchBar from "./FormsSearchBar";
import { useJsApiLoader, GoogleMap, Marker, Autocomplete as GoogleAutocomplete } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;

const containerStyle = {
  width: '100%',
  height: '300px',
};

const centerDefault = {
  lat: 18.387449,
  lng: 78.803236,
};

const libraries = ['places'];

const ALLOW_DUPLICATE_BHK = true;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// ✅ Shared styles that also turn the border red when a field has an error
const outlinedFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "& fieldset": { borderColor: "#E0E0E0" },
    "&:hover fieldset": { borderColor: "#BDBDBD" },
    "&.Mui-focused fieldset": { borderColor: "#1976D2" },
    "&.Mui-error fieldset": { borderColor: "#d32f2f" },
  },
};

const outlinedSelectSx = {
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#BDBDBD" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976D2" },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#d32f2f" },
};

const LOCATION_ERROR_MSG =
  "Please search and confirm your location (tap the ✓ button on the map)";

// Facing options
const facingOptions = [
  "East",
  "West",
  "North",
  "South",
  "North-East",
  "North-West",
  "South-East",
  "South-West",
];

// ============================================================
// Plans & Configurations: tabs / fields depend on Property Type
// ============================================================
const PLOT_TABS = ["East", "West", "North", "South", "Corner"];
const COMMERCIAL_TABS = ["Space 1", "Space 2", "Space 3", "Space 4"];
const plotStatusOptions = ["For Sale"];
const commercialStatusOptions = ["For Sale", "For Rent", "For Lease"];
const floorOptions = [
  "Basement",
  "Ground Floor",
  "1st Floor",
  "2nd Floor",
  "3rd Floor",
  "4th Floor",
  "5th Floor & Above",
  "Multiple Floors",
];
const ownershipOptions = [
  "Freehold",
  "Leasehold",
  "Co-operative Society",
  "Power of Attorney",
];

// Short icon keys expected by the backend/app (e.g. "pool", "gym", "gate").
// Add more label -> key pairs here; unknown labels fall back to a slug like "yoga-deck".
const ICON_KEYS = {
  "Swimming Pool": "pool",
  Gym: "gym",
  "Gated Community": "gate",
  "Near Metro": "metro",
};

// Property Type -> which Plans form to show: "bhk" | "plot" | "commercial"
const getPropertyKind = (type) =>
  type === "Plots/Lands"
    ? "plot"
    : type === "Commercial Spaces"
      ? "commercial"
      : "bhk";

const getDefaultTab = (kind) =>
  kind === "plot" ? "East" : kind === "commercial" ? "Space 1" : "1 BHK";

// Possession status options
const possessionStatusOptions = ["Ready to Move", "Under Construction"];

// Property types that show the Project Overview fields (Towers/Floors/Units/Total Area)
const PROJECT_OVERVIEW_TYPES = ["Apartments", "Independent Houses", "Villas"];

// Plot fields: Status, Facing, Area, Price
const plotFields = [
  { field: "status", label: "Status", type: "select", options: plotStatusOptions },
  { field: "facing", label: "Facing", type: "select", options: facingOptions },
  { field: "area", label: "Area (sqft)", type: "number", placeholder: "Enter area" },
  { field: "price", label: "Price (₹)", type: "number", placeholder: "Enter price" },
];

// Commercial space fields (from the design reference)
const commercialFields = [
  { field: "status", label: "Status", type: "select", options: commercialStatusOptions },
  { field: "price", label: "Price (₹)", type: "number", placeholder: "e.g. 18000000" },
  { field: "builtUpArea", label: "Built-up Area (sqft)", type: "number", placeholder: "e.g. 1200" },
  { field: "carpetArea", label: "Carpet Area (sqft)", type: "number", placeholder: "e.g. 950" },
  { field: "floor", label: "Floor", type: "select", options: floorOptions },
  { field: "facing", label: "Facing", type: "select", options: facingOptions },
  { field: "roadFrontage", label: "Road Frontage (feet)", type: "number", placeholder: "e.g. 30" },
  { field: "ceilingHeight", label: "Ceiling Height (feet)", type: "number", placeholder: "e.g. 12" },
  { field: "bathrooms", label: "Washrooms", type: "number", placeholder: "e.g. 2" },
  { field: "parking", label: "Parking", type: "text", placeholder: "e.g. 2 Cars" },
  { field: "powerBackup", label: "Power Backup", type: "text", placeholder: "e.g. Yes (100 KVA)" },
  { field: "ownership", label: "Ownership", type: "select", options: ownershipOptions },
];

const AddNewProperty = () => { 
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);

  // Google Maps state
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  // Location state
  const [location, setLocation] = useState(centerDefault);
  const [locationAddress, setLocationAddress] = useState("");
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [markerIcon, setMarkerIcon] = useState(null);

  // Ref for the horizontally scrollable configurations row
  const configsRowRef = useRef(null);

  // Amenity category state
  const [selectedAmenityCategory, setSelectedAmenityCategory] = useState("All");

  // ✅ Validation errors: { [fieldKey]: 'message' }
  const [errors, setErrors] = useState({});

  const clearError = (key) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  // State for form data
  const [formData, setFormData] = useState({
    // Details
    projectName: "",
    builderName: "",
    address: "",
    city: "",
    landmark: "",
    propertyType: "",
    possessionStatus: "",
    possessionDate: "",
    price: "",
    area: "",
    bedrooms: "",
    bathrooms: "",
    description: "",
    // Location fields
    location: "",
    lat: "",
    long: "",

    // Project overview fields (depend on property type)
    towers: "",
    overallFloors: "",
    units: "",
    siteArea: "",

    // Plans
    configurations: [
      {
        id: 1,
        unitType: "1 BHK", // "1 BHK" | "East" | "Space 1" ... (depends on property type)
        carpetArea: "",
        builtUpArea: "",
        superBuiltUp: "",
        price: "",
        facing: "",
        bathrooms: "", // = Washrooms for commercial
        balcony: "",
        parking: "",
        // plot + commercial fields
        status: "",
        area: "",
        floor: "",
        roadFrontage: "",
        ceilingHeight: "",
        powerBackup: "",
        ownership: "",
        floorPlan2DImages: [],
        floorPlan3DImages: [],
        isOpen: true,
      },
    ],

    // Features - Highlights
    // NOTE: these store unique KEYS (see getHighlightKey / getAmenityKey below),
    // not plain labels, so duplicate labels don't get selected together.
    highlights: [],
    amenities: [],

    // Specifications
    specifications: {
      flooring: "",
      doorsWindows: "",
      kitchen: "",
      bathroom: "",
      electrical: "",
      painting: "",
      security: "",
      smartHome: "",
    },
    customSpecifications: [],

    // Location Advantages
    locationAdvantages: [
      {
        id: 1,
        name: "",
        distance: "",
        travelTime: "",
      },
    ],

    // About Project
    aboutDescription: "",
    tags: "",

    // Builder Details
    builderDetails: {
      name: "",
      city: "",
      projectsCompleted: "",
      ongoingProjects: "",
    },

    // Brochure
    brochureTitles: "",
    brochureFiles: [],
  });

  // State for images
  const [heroImages, setHeroImages] = useState([]);
  const [featureImages, setFeatureImages] = useState([]);
  // Single logo image (stored to `logo_image`)
  const [logoImage, setLogoImage] = useState(null);

  // State for loading and alerts
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // BHK options
  const bhkOptions = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "4+ BHK"];
  const [selectedTab, setSelectedTab] = useState("1 BHK");

  // Clear hero image error automatically once an image is added
  useEffect(() => {
    if (heroImages.length > 0) clearError("heroImages");
  }, [heroImages.length]);

  // Clear location error once the pin is confirmed
  useEffect(() => {
    if (locationConfirmed) clearError("location");
  }, [locationConfirmed]);

  // Options
  const cities = [
    "Mumbai",
    "Delhi",
    "Bangalore",
    "Chennai",
    "Hyderabad",
    "Pune",
    "Kolkata",
    "Ahmedabad",
  ];
  const propertyTypes = [
    "Apartments",
    "Independent Houses",
    "Villas",
    "Plots/Lands",
    "Commercial Spaces",
  ];

  // ============================================================
  // EXPANDED HIGHLIGHT OPTIONS (from new code)
  // ============================================================
  const highlightOptions = [
    { label: "24-7 Security", icon: <Security /> },
    { label: "Power Backup", icon: <Security /> },
    { label: "Gym", icon: <FitnessCenter /> },
    { label: "Indoor Games", icon: <SportsBasketball /> },
    { label: "Jogging Track", icon: <DirectionsRun /> },
    { label: "Children Play Area", icon: <ChildCare /> },
    { label: "Senior Citizen Park", icon: <Park /> },
    { label: "Yoga Deck", icon: <SelfImprovement /> },
    { label: "Multipurpose Club Hall", icon: <WorkspacePremium /> },
    { label: "Mini Theatre", icon: <SportsScore /> },
    { label: "Co-working Space", icon: <BusinessCenter /> },
    { label: "EV Charging", icon: <Videocam /> },
    { label: "Visitor Parking", icon: <Storefront /> },
    { label: "Rain Water Harvesting", icon: <BeachAccess /> },
    { label: "Landscape Garden", icon: <Forest /> },
    { label: "CCTV", icon: <Videocam /> },
    { label: "Club House", icon: <Home /> },
    { label: "Swimming Pool", icon: <Pool /> },
    { label: "Gated Community", subtitle: "24/7 Security", icon: <Lock /> },
    { label: "2, 3 & 4 BHK", subtitle: "Configurations", icon: <Apartment /> },
    { label: "High Rise", subtitle: "Premium Living", icon: <LocationCity /> },
    { label: "Covered Parking", subtitle: "1 & 2 Cars", icon: <DirectionsCar /> },
    { label: "Swimming Pool", subtitle: "With Kids Pool", icon: <Pool /> },
    { label: "RERA Registered", subtitle: "Verified Project", icon: <Description /> },
  ];

  // ============================================================
  // EXPANDED AMENITIES OPTIONS WITH CATEGORIES (from new code)
  // ============================================================
  const amenitiesOptions = [
    // Recreation
    { label: "Swimming Pool", category: "Recreation", icon: <Pool /> },
    { label: "Kids Pool", category: "Recreation", icon: <Pool /> },
    { label: "Club House", category: "Recreation", icon: <Home /> },
    { label: "Indoor Games", category: "Recreation", icon: <SportsBasketball /> },
    { label: "Mini Theatre", category: "Recreation", icon: <Theaters /> },
    { label: "Party Hall", category: "Recreation", icon: <Celebration /> },
    { label: "Library", category: "Recreation", icon: <LocalLibrary /> },
    { label: "Cafe", category: "Recreation", icon: <LocalCafe /> },
    { label: "Guest Rooms", category: "Recreation", icon: <Hotel /> },

    // Fitness & Wellness
    { label: "Gym", category: "Fitness & Wellness", icon: <FitnessCenter /> },
    { label: "Yoga Room", category: "Fitness & Wellness", icon: <SelfImprovement /> },
    { label: "Jogging Track", category: "Fitness & Wellness", icon: <DirectionsRun /> },
    { label: "Cycling Track", category: "Fitness & Wellness", icon: <DirectionsBike /> },
    { label: "Spa", category: "Fitness & Wellness", icon: <Spa /> },
    { label: "Sauna", category: "Fitness & Wellness", icon: <Spa /> },
    { label: "Steam Room", category: "Fitness & Wellness", icon: <Spa /> },
    { label: "Meditation Area", category: "Fitness & Wellness", icon: <SelfImprovement /> },
    { label: "Fitness Centre", category: "Fitness & Wellness", icon: <FitnessCenter /> },

    // Sports
    { label: "Tennis Court", category: "Sports", icon: <SportsTennis /> },
    { label: "Basketball Court", category: "Sports", icon: <SportsBasketball /> },
    { label: "Badminton Court", category: "Sports", icon: <SportsTennis /> },
    { label: "Cricket Net", category: "Sports", icon: <SportsCricket /> },
    { label: "Football Field", category: "Sports", icon: <SportsSoccer /> },
    { label: "Table Tennis", category: "Sports", icon: <SportsTennis /> },
    { label: "Volleyball Court", category: "Sports", icon: <SportsVolleyball /> },
    { label: "Skating Area", category: "Sports", icon: <RollerSkating /> },
    { label: "Kids Play Area", category: "Sports", icon: <ChildCare /> },

    // Security
    { label: "24x7 Security", category: "Security", icon: <Security /> },
    { label: "CCTV Surveillance", category: "Security", icon: <Videocam /> },
    { label: "Video Door Phone", category: "Security", icon: <Doorbell /> },
    { label: "Intercom", category: "Security", icon: <Phone /> },
    { label: "Gated Entry", category: "Security", icon: <DoorFront /> },
    { label: "Visitor Mgmt", category: "Security", icon: <People /> },
    { label: "Fire Safety", category: "Security", icon: <LocalFireDepartment /> },
    { label: "Access Control", category: "Security", icon: <Lock /> },
    { label: "Emergency Exit", category: "Security", icon: <ExitToApp /> },

    // Utilities
    { label: "Power Backup", category: "Utilities", icon: <Bolt /> },
    { label: "Water Supply", category: "Utilities", icon: <WaterDrop /> },
    { label: "Rainwater Harvesting", category: "Utilities", icon: <WaterDrop /> },
    { label: "Sewage Treatment", category: "Utilities", icon: <Business /> },
    { label: "Waste Mgmt", category: "Utilities", icon: <DeleteOutline /> },
    { label: "Solar Power", category: "Utilities", icon: <WbSunny /> },
    { label: "EV Charging", category: "Utilities", icon: <EvStation /> },
    { label: "Gas Pipeline", category: "Utilities", icon: <LocalGasStation /> },
    { label: "STP / WTP", category: "Utilities", icon: <Business /> },

    // Building Facilities
    { label: "High Speed Lift", category: "Building Facilities", icon: <Elevator /> },
    { label: "Lobby", category: "Building Facilities", icon: <Business /> },
    { label: "Reception", category: "Building Facilities", icon: <SupportAgent /> },
    { label: "Waiting Area", category: "Building Facilities", icon: <Chair /> },
    { label: "Basement Parking", category: "Building Facilities", icon: <DirectionsCar /> },
    { label: "Maintenance", category: "Building Facilities", icon: <Build /> },
    { label: "Housekeeping", category: "Building Facilities", icon: <CleaningServices /> },
    { label: "Service Lift", category: "Building Facilities", icon: <Elevator /> },
    { label: "Common Terrace", category: "Building Facilities", icon: <Deck /> },

    // Family & Convenience
    { label: "Kids Play Area", category: "Family & Convenience", icon: <ChildCare /> },
    { label: "Creche/Day Care", category: "Family & Convenience", icon: <ChildFriendly /> },
    { label: "Senior Citizen Area", category: "Family & Convenience", icon: <Park /> },
    { label: "Convenience Store", category: "Family & Convenience", icon: <Storefront /> },
    { label: "Pharmacy", category: "Family & Convenience", icon: <LocalPharmacy /> },
    { label: "ATM", category: "Family & Convenience", icon: <LocalAtm /> },
    { label: "Salon", category: "Family & Convenience", icon: <ContentCut /> },
    { label: "Laundry", category: "Family & Convenience", icon: <LocalLaundryService /> },
    { label: "Medical Centre", category: "Family & Convenience", icon: <LocalHospital /> },
  ];

  // Amenity categories for tabs
  const amenityCategories = [
    "All",
    "Recreation",
    "Fitness & Wellness",
    "Sports",
    "Security",
    "Utilities",
    "Building Facilities",
    "Family & Convenience",
  ];

  // ------------------------------------------------------------
  // FIX: helpers for unique selection keys + icon slug
  // ------------------------------------------------------------
  const slugify = (s) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getIconKey = (label) => ICON_KEYS[label] || slugify(label);

  // ✅ DOM id used to scroll to a field
  const fieldId = (key) => `field-${slugify(String(key))}`;

  // Unique keys, so duplicate labels don't get selected together
  const getHighlightKey = (item) => `${item.label}|${item.subtitle || ""}`;
  const getAmenityKey = (item) => `${item.category}|${item.label}`;

  // Specification fields
  const specificationFields = [
    { key: "flooring", label: "Flooring", icon: <Home /> },
    { key: "doorsWindows", label: "Doors & Windows", icon: <DoorFront /> },
    { key: "kitchen", label: "Kitchen", icon: <Kitchen /> },
    { key: "bathroom", label: "Bathroom", icon: <Bathroom /> },
    { key: "electrical", label: "Electrical", icon: <ElectricalServices /> },
    { key: "painting", label: "Painting", icon: <FormatPaint /> },
    { key: "security", label: "Security", icon: <Security /> },
    { key: "smartHome", label: "Smart Home Features", icon: <SmartToy /> },
  ];

  // Set marker icon
  useEffect(() => {
    if (isLoaded && window.google) {
      setMarkerIcon({
        url: "data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22%23ff0000%22%20d%3D%22M12%202C8.13%202%205%205.13%205%209c0%205.25%207%2013%207%2013s7-7.75%207-13c0-3.87-3.13-7-7-7zm0%209.5c-1.38%200-2.5-1.12-2.5-2.5s1.12-2.5%202.5-2.5%202.5%201.12%202.5%202.5-1.12%202.5-2.5%202.5z%22%2F%3E%3C%2Fsvg%3E",
        scaledSize: new window.google.maps.Size(40, 40),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(20, 40)
      });
    }
  }, [isLoaded]);

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    setUsingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setLocation(pos);

        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${pos.lat},${pos.lng}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();

          if (data.status === 'OK' && data.results && data.results.length > 0) {
            const locationAddress = data.results[0].formatted_address;
            setLocationAddress(locationAddress);
            setFormData(prev => ({
              ...prev,
              location: locationAddress,
              lat: pos.lat.toString(),
              long: pos.lng.toString(),
            }));
            setLocationConfirmed(true);
          } else {
            console.warn('No address found, fallback to coordinates.');
            const fallback = `Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`;
            setLocationAddress(fallback);
            setFormData(prev => ({
              ...prev,
              location: fallback,
              lat: pos.lat.toString(),
              long: pos.lng.toString(),
            }));
            setLocationConfirmed(true);
          }
        } catch (error) {
          console.error('Google Maps API error:', error);
          const fallback = `Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`;
          setLocationAddress(fallback);
          setFormData(prev => ({
            ...prev,
            location: fallback,
            lat: pos.lat.toString(),
            long: pos.lng.toString(),
          }));
          setLocationConfirmed(true);
        }
        setUsingCurrentLocation(false);
      },
      (error) => {
        console.error("Error getting current location:", error);
        alert("Error getting current location. Please enable location services.");
        setUsingCurrentLocation(false);
      }
    );
  };

  // Handle place changed from autocomplete
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        setUsingCurrentLocation(false);
        const newLoc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setLocation(newLoc);
        setLocationAddress(place.formatted_address);
        setFormData(prev => ({
          ...prev,
          location: place.formatted_address,
          lat: newLoc.lat.toString(),
          long: newLoc.lng.toString(),
        }));
        setLocationConfirmed(true);
      }
    }
  };

  // Handle map click
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
    setUsingCurrentLocation(false);
    setLocationConfirmed(false);

    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const locationAddress = results[0].formatted_address;
          setLocationAddress(locationAddress);
          setFormData(prev => ({
            ...prev,
            location: locationAddress,
            lat: lat.toString(),
            long: lng.toString(),
          }));
        } else {
          const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
          setLocationAddress(fallback);
          setFormData(prev => ({
            ...prev,
            location: fallback,
            lat: lat.toString(),
            long: lng.toString(),
          }));
        }
      });
    }
  };

  // Confirm location from map center
  const confirmLocation = () => {
    if (window.google && window.google.maps && mapRef.current) {
      const center = mapRef.current.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      setLocation({ lat, lng });

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const selectedAddress = results[0].formatted_address;
            setLocationAddress(selectedAddress);
            setFormData(prev => ({
              ...prev,
              location: selectedAddress,
              lat: lat.toString(),
              long: lng.toString(),
            }));
          } else {
            const fallback = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
            setLocationAddress(fallback);
            setFormData(prev => ({
              ...prev,
              location: fallback,
              lat: lat.toString(),
              long: lng.toString(),
            }));
          }
          setLocationConfirmed(true);
        }
      );
    }
  };

  // Handle input change
  const handleInputChange = (field) => (event) => {
    clearError(field);
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Handle builder details change
  const handleBuilderChange = (field) => (event) => {
    setFormData({
      ...formData,
      builderDetails: {
        ...formData.builderDetails,
        [field]: event.target.value,
      },
    });
  };

  // Handle specification change
  const handleSpecificationChange = (field) => (event) => {
    setFormData({
      ...formData,
      specifications: {
        ...formData.specifications,
        [field]: event.target.value,
      },
    });
  };

  // Handle custom specification
  const addCustomSpecification = () => {
    setFormData({
      ...formData,
      customSpecifications: [
        ...formData.customSpecifications,
        { id: Date.now(), key: "", value: "" },
      ],
    });
  };

  const removeCustomSpecification = (id) => {
    setFormData({
      ...formData,
      customSpecifications: formData.customSpecifications.filter(
        (item) => item.id !== id,
      ),
    });
  };

  const updateCustomSpecification = (id, field, value) => {
    setFormData({
      ...formData,
      customSpecifications: formData.customSpecifications.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    });
  };

  // Handle location advantages
  const addLocationAdvantage = () => {
    const newId =
      Math.max(...formData.locationAdvantages.map((l) => l.id), 0) + 1;
    setFormData({
      ...formData,
      locationAdvantages: [
        ...formData.locationAdvantages,
        { id: newId, name: "", distance: "", travelTime: "" },
      ],
    });
  };

  const removeLocationAdvantage = (id) => {
    setFormData({
      ...formData,
      locationAdvantages: formData.locationAdvantages.filter(
        (item) => item.id !== id,
      ),
    });
  };

  const updateLocationAdvantage = (id, field, value) => {
    clearError(`loc-${id}-${field}`);
    setFormData({
      ...formData,
      locationAdvantages: formData.locationAdvantages.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    });
  };

  // ============================================================
  // CONFIGURATIONS (tabs + fields depend on Property Type)
  // ============================================================

  // Which Plans form to show: "bhk" | "plot" | "commercial"
  const propertyKind = getPropertyKind(formData.propertyType);

  // Tabs: BHK tabs / plot direction tabs / Space 1-4 for commercial
  const planTabs =
    propertyKind === "plot"
      ? PLOT_TABS
      : propertyKind === "commercial"
        ? COMMERCIAL_TABS
        : bhkOptions;

  // Villa shows "1 BHK Villa"; everything else shows the tab text as-is
  const getTabLabel = (tab) =>
    formData.propertyType === "Villas" ? `${tab} Villas` : tab;

  // Configurations shown for the current tab
  const visibleConfigs = formData.configurations.filter(
    (c) => c.unitType === selectedTab,
  );

  const plansHeading =
    propertyKind === "commercial"
      ? `Commercial Space Details (${selectedTab})`
      : propertyKind === "plot"
        ? `Plot Details (${selectedTab})`
        : `Configurations (${getTabLabel(selectedTab)} Types)`;

  // Blank configuration factory
  const createEmptyConfig = (id, unitType) => ({
    id,
    unitType,
    carpetArea: "",
    builtUpArea: "",
    superBuiltUp: "",
    price: "",
    // plot tabs East/West/North/South pre-fill the Facing field (editable)
    facing: ["East", "West", "North", "South"].includes(unitType) ? unitType : "",
    bathrooms: "",
    balcony: "",
    parking: "",
    status: "",
    area: "",
    floor: "",
    roadFrontage: "",
    ceilingHeight: "",
    powerBackup: "",
    ownership: "",
    floorPlan2DImages: [],
    floorPlan3DImages: [],
    isOpen: true,
  });

  // A config counts as "filled" if the user entered anything in it
  const isConfigFilled = (c) =>
    c.carpetArea ||
    c.builtUpArea ||
    c.superBuiltUp ||
    c.price ||
    c.area ||
    c.status ||
    c.floor ||
    c.roadFrontage ||
    c.ceilingHeight ||
    c.powerBackup ||
    c.ownership ||
    c.floorPlan2DImages.length > 0 ||
    c.floorPlan3DImages.length > 0;

  // Price per sqft (auto) for commercial spaces
  const getPricePerSqft = (c) => {
    const price = parseFloat(c.price);
    const area = parseFloat(c.builtUpArea);
    return price > 0 && area > 0 ? Math.round(price / area) : null;
  };

  // Property Type dropdown: reset the Plans section when the form type changes
  const handlePropertyTypeChange = (event) => {
    const newType = event.target.value;
    const oldKind = getPropertyKind(formData.propertyType);
    const newKind = getPropertyKind(newType);

    // Same kind (e.g. Apartment <-> Villa): keep the entered data
    if (oldKind === newKind) {
      clearError("propertyType");
      setFormData((prev) => ({ ...prev, propertyType: newType }));
      return;
    }

    if (
      formData.configurations.some(isConfigFilled) &&
      !window.confirm(
        "Changing the property type will clear the Plans & Configurations you entered. Continue?",
      )
    ) {
      return;
    }

    clearError("propertyType");

    // Old configuration errors no longer apply after the Plans section is reset
    setErrors((prev) => {
      const rest = {};
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith("cfg-")) rest[k] = prev[k];
      });
      return rest;
    });

    const tab = getDefaultTab(newKind);
    setSelectedTab(tab);
    setFormData((prev) => ({
      ...prev,
      propertyType: newType,
      configurations: [createEmptyConfig(1, tab)],
    }));
  };

  // Switching tab: create a blank form if this tab has none yet
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setFormData((prev) => {
      if (prev.configurations.some((c) => c.unitType === tab)) return prev;
      const newId = Math.max(...prev.configurations.map((c) => c.id), 0) + 1;
      return {
        ...prev,
        configurations: [...prev.configurations, createEmptyConfig(newId, tab)],
      };
    });
  };

  // Handle configuration changes
  const handleConfigChange = (id, field, value) => {
    clearError(`cfg-${id}-${field}`);
    setFormData((prev) => ({
      ...prev,
      configurations: prev.configurations.map((config) =>
        config.id === id ? { ...config, [field]: value } : config,
      ),
    }));
  };

  // Add another configuration under the currently selected tab
  // ✅ Also scrolls the horizontal row to the newly added card.
  const addConfiguration = () => {
    setFormData((prev) => {
      const newId = Math.max(...prev.configurations.map((c) => c.id), 0) + 1;
      return {
        ...prev,
        configurations: [
          ...prev.configurations,
          createEmptyConfig(newId, selectedTab),
        ],
      };
    });

    // Scroll the horizontal row to the end after React paints the new card
    setTimeout(() => {
      const el = configsRowRef.current;
      if (el) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }, 60);
  };

  // Remove configuration (each tab keeps at least one form)
  const removeConfiguration = (id) => {
    if (visibleConfigs.length <= 1) {
      alert(`At least one ${getTabLabel(selectedTab)} configuration is required`);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      configurations: prev.configurations.filter((config) => config.id !== id),
    }));
  };

  // Toggle configuration accordion
  const toggleConfig = (id) => {
    setFormData((prev) => ({
      ...prev,
      configurations: prev.configurations.map((config) =>
        config.id === id ? { ...config, isOpen: !config.isOpen } : config,
      ),
    }));
  };

  // Floor plan images live on each configuration (type = "2D" | "3D")
  const getFloorPlanField = (type) =>
    type === "2D" ? "floorPlan2DImages" : "floorPlan3DImages";

  // Each configuration allows only ONE 2D and ONE 3D floor plan image.
  // Selecting a new file replaces whatever was uploaded before.
  const handleFloorPlanUpload = (id, type) => (event) => {
    const field = getFloorPlanField(type);
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (files.length > 1) {
      alert(`Only one ${type} floor plan image is allowed. The first selected file will be used.`);
    }

    const file = files[0];
    const newImage = { file, url: URL.createObjectURL(file) };

    setFormData((prev) => ({
      ...prev,
      configurations: prev.configurations.map((config) =>
        config.id === id ? { ...config, [field]: [newImage] } : config,
      ),
    }));

    // allow selecting the same file again later
    event.target.value = "";
  };

  const removeFloorPlanImage = (configId, type, imageIndex) => {
    const field = getFloorPlanField(type);
    setFormData((prev) => ({
      ...prev,
      configurations: prev.configurations.map((config) =>
        config.id === configId
          ? {
              ...config,
              [field]: config[field].filter((_, i) => i !== imageIndex),
            }
          : config,
      ),
    }));
  };

 const handleBrochureUpload = (event) => {
  const file = event.target.files?.[0];

  if (!file) return;

  // Only allow PDF
  if (file.type !== "application/pdf") {
    alert("Please upload only a PDF file.");
    event.target.value = "";
    return;
  }

  // Only allow one file
  if (formData.brochureFiles.length >= 1) {
    alert("You can upload only one PDF file.");
    event.target.value = "";
    return;
  }

  const newFile = {
    file,
    url: URL.createObjectURL(file),
    name: file.name,
  };

  setFormData((prev) => ({
    ...prev,
    brochureFiles: [newFile],
  }));

  // Clear input so same file can be selected again after removing
  event.target.value = "";
};

  const removeBrochure = (index) => {
    setFormData((prev) => ({
      ...prev,
      brochureFiles: prev.brochureFiles.filter((_, i) => i !== index),
    }));
  };

 const handleImageUpload = (type) => (event) => {
  const files = Array.from(event.target.files);
  const newImages = files.map((file) => ({
    file,
    url: URL.createObjectURL(file),
  }));

  if (type === "hero") {
    if (heroImages.length + files.length > 5) {
      alert("Maximum 5 images allowed");
      event.target.value = "";
      return;
    }
    setHeroImages([...heroImages, ...newImages]);
  } else if (type === "feature") {
    if (featureImages.length + files.length > 5) {
      alert("Maximum 5 images allowed");
      event.target.value = "";
      return;
    }
    setFeatureImages([...featureImages, ...newImages]);
  }

  event.target.value = ""; // allow selecting the same file again
};

 const removeImage = (type, index) => {
  const list = type === "hero" ? heroImages : featureImages;
  if (list[index]) URL.revokeObjectURL(list[index].url);

  if (type === "hero") {
    setHeroImages(heroImages.filter((_, i) => i !== index));
  } else if (type === "feature") {
    setFeatureImages(featureImages.filter((_, i) => i !== index));
  }
};

  // Handle logo image upload (single image -> logo_image)
  const handleLogoUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    const file = files[0];
    setLogoImage({ file, url: URL.createObjectURL(file) });
    event.target.value = "";
  };

  const removeLogoImage = () => {
    setLogoImage(null);
  };

  // Handle highlight toggle (value = unique highlight key)
  const toggleHighlight = (key) => {
    setFormData((prev) => {
      const isSelected = prev.highlights.includes(key);
      const updatedHighlights = isSelected
        ? prev.highlights.filter((h) => h !== key)
        : [...prev.highlights, key];

      return {
        ...prev,
        highlights: updatedHighlights,
      };
    });
  };

  // Handle amenity toggle (value = unique amenity key)
  const toggleAmenity = (key) => {
    setFormData((prev) => {
      const isSelected = prev.amenities.includes(key);
      const updatedAmenities = isSelected
        ? prev.amenities.filter((a) => a !== key)
        : [...prev.amenities, key];

      return {
        ...prev,
        amenities: updatedAmenities,
      };
    });
  };

  // Convert BHK type to match API format.
  // Only known "N BHK" labels get their space stripped (-> "1BHK").
  // Plot directions (East/West/...) and commercial spaces (Space 1/2/...)
  // are sent exactly as-is, so "Space 1" is NOT turned into "Space1".
  const formatBHKType = (bhk) => {
    const map = {
      "1 BHK": "1BHK",
      "2 BHK": "2BHK",
      "3 BHK": "3BHK",
      "4 BHK": "4BHK",
      "4+ BHK": "4+BHK",
    };
    return map[bhk] || bhk;
  };

  // ============================================================
  // DEBUG: print the multipart payload in a readable way
  // ============================================================
  const logPayload = (fd) => {
    const grouped = {};
    for (const [key, value] of fd.entries()) {
      let v = value;
      if (value instanceof File) {
        v = `[File] ${value.name} (${(value.size / 1024).toFixed(1)} KB)`;
      } else if (typeof value === "string" && /^[\[{]/.test(value)) {
        try {
          v = JSON.parse(value);
        } catch {
          /* keep as string */
        }
      }
      (grouped[key] = grouped[key] || []).push(v);
    }
    const readable = Object.fromEntries(
      Object.entries(grouped).map(([k, arr]) => [
        k,
        arr.length === 1 ? arr[0] : arr,
      ]),
    );

    console.group(`📦 POST ${BASE_URL}/ad-properties/combined/`);
    console.log(readable);
    console.log("Highlights:");
    console.table(readable.property_highlights || []);
    console.log("Amenities:");
    console.table(readable.property_amenities || []);
    console.log("Configurations:");
    console.table(readable.configurations || []);
    console.groupEnd();
  };

  // ============================================================
  // ✅ FORM VALIDATION
  // Errors are inserted in on-screen order, so the first key is
  // always the top-most invalid field.
  // ============================================================
  const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";

  const validateForm = () => {
    const errs = {};

    // 1. Project & Builder Details
    if (isEmpty(formData.projectName)) errs.projectName = "Project name is required";
    if (isEmpty(formData.builderName)) errs.builderName = "Builder name is required";
    if (isEmpty(formData.address)) errs.address = "Address is required";
    if (isEmpty(formData.city)) errs.city = "Please select a city";
    if (isEmpty(formData.propertyType)) errs.propertyType = "Please select a property type";
    if (isEmpty(formData.possessionStatus)) errs.possessionStatus = "Please select a possession status";

    // Possession Date is required only when the project is under construction
    if (
      formData.possessionStatus === "Under Construction" &&
      isEmpty(formData.possessionDate)
    ) {
      errs.possessionDate = "Please enter the possession date";
    }

    // Location (remove the `locationConfirmed` check if you don't want to force the pin confirm)
    if (!locationConfirmed || !formData.lat || !formData.long) {
      errs.location = LOCATION_ERROR_MSG;
    }

    // Hero images
    if (heroImages.length === 0) errs.heroImages = "Please upload at least one project image";

    // 2. Plans & Configurations (BHK types: fields marked * are required
    //    for every configuration the user has started filling in)
    if (propertyKind === "bhk") {
      planTabs.forEach((tab) => {
        formData.configurations
          .filter((c) => c.unitType === tab && isConfigFilled(c))
          .forEach((c) => {
            if (isEmpty(c.carpetArea)) errs[`cfg-${c.id}-carpetArea`] = "Carpet area is required";
            if (isEmpty(c.builtUpArea)) errs[`cfg-${c.id}-builtUpArea`] = "Built-up area is required";
            if (isEmpty(c.price)) errs[`cfg-${c.id}-price`] = "Price is required";
            if (isEmpty(c.bathrooms)) errs[`cfg-${c.id}-bathrooms`] = "Bathrooms is required";
            if (isEmpty(c.balcony)) errs[`cfg-${c.id}-balcony`] = "Balcony is required";
          });
      });
    }

    // 5. Location Advantages: a row is optional, but if the user started
    //    filling it in, all three fields are required
    formData.locationAdvantages.forEach((place) => {
      const started =
        !isEmpty(place.name) || !isEmpty(place.distance) || !isEmpty(place.travelTime);
      if (!started) return;
      if (isEmpty(place.name)) errs[`loc-${place.id}-name`] = "Place name is required";
      if (isEmpty(place.distance)) errs[`loc-${place.id}-distance`] = "Distance is required";
      if (isEmpty(place.travelTime)) errs[`loc-${place.id}-travelTime`] = "Travel time is required";
    });

    return errs;
  };

  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;

    let delay = 0;

    // Configuration errors: switch to that tab / open the accordion first
    if (firstKey.startsWith("cfg-")) {
      const cfgId = parseInt(firstKey.split("-")[1], 10);
      const cfg = formData.configurations.find((c) => c.id === cfgId);
      if (cfg) {
        if (cfg.unitType !== selectedTab) {
          setSelectedTab(cfg.unitType);
          delay = 350;
        }
        if (!cfg.isOpen) {
          setFormData((prev) => ({
            ...prev,
            configurations: prev.configurations.map((c) =>
              c.id === cfgId ? { ...c, isOpen: true } : c,
            ),
          }));
          delay = 350;
        }
      }
    }

    setTimeout(() => {
      const el = document.getElementById(fieldId(firstKey));
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        el.querySelector('input, textarea, [role="combobox"], [role="button"]')?.focus({
          preventScroll: true,
        });
      }, 400);
    }, delay);
  };

  // Submit form
  const handleSubmit = async () => {
    // ✅ Run validation, show red messages and scroll to the first invalid field
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    // Validate duplicate tabs (backend allows only one config per BHK type / tab).
    if (!ALLOW_DUPLICATE_BHK) {
      const counts = {};
      formData.configurations.filter(isConfigFilled).forEach((c) => {
        counts[c.unitType] = (counts[c.unitType] || 0) + 1;
      });
      const duplicates = Object.keys(counts).filter((k) => counts[k] > 1);
      if (duplicates.length > 0) {
        alert(
          `You have more than one ${duplicates.map(getTabLabel).join(", ")} configuration. ` +
            `Only one configuration per tab is allowed. ` +
            `Please remove the extra one(s) and submit again.`
        );
        return;
      }
    }

    setLoading(true);

    try {
      // Get user_id from localStorage
      const userIdFromStorage = localStorage.getItem("user_id");
      const finalUserId = userIdFromStorage || userId;

      if (!finalUserId) {
        alert("User ID not found. Please login again.");
        setLoading(false);
        return;
      }

      // Create FormData for multipart upload
      const formDataToSend = new FormData();

      // MAIN PROPERTY FIELDS
      formDataToSend.append("user_id", parseInt(finalUserId).toString());
      formDataToSend.append("category_id", "56");
      formDataToSend.append("property_name", formData.projectName);
      formDataToSend.append("property_type", formData.propertyType);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("city", formData.city);
      formDataToSend.append("landmark", formData.landmark || "");
      formDataToSend.append("possession_status", formData.possessionStatus);
      // Only send possession_date when it's actually relevant (Under Construction)
      formDataToSend.append(
        "possession_date",
        formData.possessionStatus === "Under Construction"
          ? formData.possessionDate
          : ""
      );
      formDataToSend.append("description", formData.aboutDescription || formData.description || "");
      formDataToSend.append("tags", formData.tags || "");
      
      // Location fields
      formDataToSend.append("location", formData.location || "");
      formDataToSend.append("lat", formData.lat);
      formDataToSend.append("long", formData.long);

      // Project overview fields (Towers/Floors for Apartment/Independent House/Villa,
      // Units/Total Area for those + Plot/Land)
      formDataToSend.append("towers", formData.towers || "");
      formDataToSend.append("overall_floors", formData.overallFloors || "");
      formDataToSend.append("units", formData.units || "");
      formDataToSend.append("site_area", formData.siteArea || "");

      // Logo image (single file)
      if (logoImage) {
        formDataToSend.append("logo_image", logoImage.file);
      }

      // Builder Details
      formDataToSend.append(
        "builder_details",
        JSON.stringify({
          builder_name: formData.builderName,
          city: formData.builderDetails.city || "",
          projects_completed: parseInt(formData.builderDetails.projectsCompleted) || 0,
          ongoing_projects: parseInt(formData.builderDetails.ongoingProjects) || 0,
        })
      );

      // Configurations - only send the ones the user actually filled in
      // (visiting a BHK tab creates a blank form, which we skip here)
      const configsToSend = formData.configurations.filter(isConfigFilled);

      // "bhk_type" carries the unit label: 1BHK / East / Space 1 ...
      const getApiUnitType = (config) => formatBHKType(config.unitType);

      formDataToSend.append(
        "configurations",
        JSON.stringify(
          configsToSend.map((config) => ({
            // ---- existing fields (always sent) ----
            bhk_type: getApiUnitType(config),
            carpet_area: parseFloat(config.carpetArea) || 0,
            built_up_area: parseFloat(config.builtUpArea) || 0,
            super_built_up_area: parseFloat(config.superBuiltUp) || 0,
            price: parseFloat(config.price) || 0,
            facing: config.facing || "",
            // commercial "Washrooms" goes to its own `washrooms` field (below)
            bathrooms:
              propertyKind === "commercial" ? 0 : parseInt(config.bathrooms) || 0,
            balcony: parseInt(config.balcony) || 0,
            parking: config.parking || "",

            // ---- NEW fields: backend must accept these ----
            ...(propertyKind === "plot" && {
              status: config.status || "",
              // The model has no "area" column — Plot "Area" is stored in
              // carpet_area (overrides the 0 default set above).
              carpet_area: parseFloat(config.area) || 0,
            }),
            ...(propertyKind === "commercial" && {
              status: config.status || "",
              floor: config.floor || "",
              washrooms: parseInt(config.bathrooms) || 0,
              road_frontage: parseFloat(config.roadFrontage) || 0,
              ceiling_height: parseFloat(config.ceilingHeight) || 0,
              power_backup: config.powerBackup || "",
              ownership: config.ownership || "",
            }),
          }))
        )
      );

      // Location Advantages
// Send [] when the user has not entered any location advantage.
const locationAdvantagesPayload = formData.locationAdvantages
  .filter(
    (place) =>
      place.name?.trim() ||
      place.distance?.trim() ||
      place.travelTime?.trim()
  )
  .map((place, index) => ({
    place_name: place.name.trim(),
    distance: place.distance.trim(),
    travel_time: place.travelTime.trim(),
    order: index + 1,
  }));

formDataToSend.append(
  "location_advantages",
  JSON.stringify(locationAdvantagesPayload)
);

      // Property Highlights (now includes `icon`)
      const highlightPayload = formData.highlights
        .map((key) => highlightOptions.find((o) => getHighlightKey(o) === key))
        .filter(Boolean)
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      // Sent under both names until the backend key is confirmed
      // (response uses "property_highlights", the API sample uses "highlights").
      formDataToSend.append("property_highlights", JSON.stringify(highlightPayload));
      formDataToSend.append("highlights", JSON.stringify(highlightPayload));

      // Property Amenities (now includes `icon`, deduped by name because
      // e.g. "Kids Play Area" exists in two categories)
      const seenAmenities = new Set();
      const amenityPayload = formData.amenities
        .map((key) => amenitiesOptions.find((o) => getAmenityKey(o) === key))
        .filter(
          (item) =>
            item && !seenAmenities.has(item.label) && seenAmenities.add(item.label)
        )
        .map((item, index) => ({
          name: item.label,
          icon: getIconKey(item.label),
          order: index + 1,
          is_active: true,
        }));
      formDataToSend.append("property_amenities", JSON.stringify(amenityPayload));
      formDataToSend.append("amenities", JSON.stringify(amenityPayload));

      // Specifications
      formDataToSend.append(
        "specifications",
        JSON.stringify([
          ...Object.entries(formData.specifications)
            .filter(([_, value]) => value && value.trim() !== "")
            .map(([key, value]) => ({
              name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
              value: value,
            })),
          ...formData.customSpecifications
            .filter(
              (item) =>
                item.key &&
                item.key.trim() !== "" &&
                item.value &&
                item.value.trim() !== ""
            )
            .map((item) => ({
              name: item.key,
              value: item.value,
            })),
        ])
      );

      // Hero Images
      heroImages.forEach((img) => {
        formDataToSend.append("hero_images", img.file);
      });

      // Feature Images
      featureImages.forEach((img) => {
        formDataToSend.append("feature_images", img.file);
      });

      // Floor Plan Files - 2D and 3D, per configuration
      // (index MUST match the filtered configsToSend order)
      configsToSend.forEach((config, index) => {
        config.floorPlan2DImages.forEach((img) => {
          formDataToSend.append(`configuration_${index}_2d`, img.file);
        });
        config.floorPlan3DImages.forEach((img) => {
          formDataToSend.append(`configuration_${index}_3d`, img.file);
        });
      });

      // Brochures
      formData.brochureFiles.forEach((file) => {
        const title = file.name.split('.')[0] || "Brochure";
        formDataToSend.append("brochure_titles", title);
        formDataToSend.append("brochures", file.file);
      });

      // DEBUG: show exactly what is being sent
      logPayload(formDataToSend);

      // POST
      const response = await axios.post(
        `${BASE_URL}/ad-properties/combined/`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // DEBUG: show what the server saved (API wraps result in `data`)
      const saved = response.data?.data || response.data;
      console.log("✅ API response:", response.data);
      console.log(
        "Saved → highlights:",
        saved?.property_highlights?.length,
        "| amenities:",
        saved?.property_amenities?.length,
        "| configurations:",
        saved?.configurations?.length
      );
      (saved?.configurations || []).forEach((c) =>
        console.log(
          `Config #${c.id} ${c.bhk_type} → floor plans:`,
          (c.floor_plan_images || []).map((f) => f.plan_type)
        )
      );

      console.log(
        "Image URLs → hero:",
        saved?.hero_images?.[0]?.image,
        "| floor plan:",
        saved?.configurations?.[0]?.floor_plan_images?.[0]?.image
      );

      if (response.status === 201 || response.status === 200) {
        setAlertMessage("Property added successfully!");
        setShowSuccess(true);
      } else {
        setAlertMessage("Something went wrong. Please try again.");
        setShowFailure(true);
      }
    } catch (error) {
      console.error("Error submitting property:", error);
      if (error.response?.data) {
        console.error("Server response:", error.response.data);
        const serverMsg = error.response.data.message || "Error submitting property";
        setAlertMessage(
          String(serverMsg).includes("unique_property_bhk_configuration")
            ? "The server only allows one configuration per BHK type. The backend unique constraint 'unique_property_bhk_configuration' must be removed to save multiple."
            : serverMsg
        );
      } else {
        setAlertMessage("Error submitting property. Please try again.");
      }
      setShowFailure(true);
    } finally {
      setLoading(false);
    }
  };

  // Render image upload section (with preview of selected images)
const renderImageUpload = (type, images, title, maxCount = 5) => {
  const uploadId = `${type}-upload`;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          border: `2px dashed ${type === "hero" && errors.heroImages ? "#d32f2f" : "#D0D0D0"}`,
          borderRadius: "8px",
          p: 3,
          textAlign: "center",
          bgcolor: "#FAFAFA",
          cursor: "pointer",
          "&:hover": {
            bgcolor: "#F0F0F0",
            borderColor: "#B0B0B0",
          },
        }}
        onClick={() => document.getElementById(uploadId).click()}
      >
        <Add sx={{ fontSize: "40px", color: "#999" }} />
        <Typography sx={{ fontSize: "13px", color: "#666", mt: 1 }}>
          {title || "Upload Images"}
        </Typography>
        <input
          id={uploadId}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload(type)}
          style={{ display: "none" }}
        />
      </Box>

      {/* Validation message for the hero images */}
      {type === "hero" && errors.heroImages && (
        <Typography
          variant="caption"
          sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}
        >
          {errors.heroImages}
        </Typography>
      )}

     {/* Preview box - compact thumbnails */}
{images.length > 0 && (
  <Box
    sx={{
      mt: 1,
      p: 1,
      border: "1px solid #E0E0E0",
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
    }}
  >
    <Typography sx={{ fontSize: "11px", color: "#666", mb: 0.5 }}>
      {images.length} / {maxCount} image(s) selected
    </Typography>

    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {images.map((img, index) => (
        <Box
          key={`${img.url}-${index}`}
          sx={{
            position: "relative",
            width: "64px",
            height: "64px",
            borderRadius: "6px",
            overflow: "hidden",
            border: "1px solid #E0E0E0",
            flexShrink: 0,
          }}
        >
          <img
            src={img.url}
            alt={`${type} preview ${index + 1}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              removeImage(type, index);
            }}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFF",
              padding: "1px",
              borderRadius: "0 0 0 6px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
            }}
          >
            <Close sx={{ fontSize: "12px" }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  </Box>
)}
    </Box>
  );
};

  // Render single Logo image upload
  const renderLogoUpload = () => (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
      >
        Logo Image
      </Typography>
      {logoImage ? (
        <Box
          sx={{
            position: "relative",
            width: "96px",
            height: "96px",
            borderRadius: "8px",
            overflow: "hidden",
            border: "2px solid #E0E0E0",
          }}
        >
          <img
            src={logoImage.url}
            alt="Logo"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <IconButton
            size="small"
            onClick={removeLogoImage}
            sx={{
              position: "absolute",
              top: "2px",
              right: "2px",
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFF",
              padding: "2px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
            }}
          >
            <Close sx={{ fontSize: "14px" }} />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            border: "2px dashed #D0D0D0",
            borderRadius: "8px",
            width: "96px",
            height: "96px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            bgcolor: "#FAFAFA",
            "&:hover": { bgcolor: "#F0F0F0", borderColor: "#B0B0B0" },
          }}
          onClick={() => document.getElementById("logo-upload").click()}
        >
          <Add sx={{ fontSize: "28px", color: "#999" }} />
          <Typography sx={{ fontSize: "10px", color: "#666", mt: 0.5 }}>
            Upload
          </Typography>
        </Box>
      )}
      <input
        id="logo-upload"
        type="file"
        accept="image/*"
        onChange={handleLogoUpload}
        style={{ display: "none" }}
      />
    </Box>
  );

  // Render Details section with Location Map
  const renderDetailsSection = () => (
    <Box>
      {/* Project Name */}
      <Box sx={{ mb: 2 }} id={fieldId("projectName")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Project Name <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter project name"
          value={formData.projectName}
          onChange={handleInputChange("projectName")}
          size="small"
          error={!!errors.projectName}
          helperText={errors.projectName}
          sx={outlinedFieldSx}
        />
      </Box>

      {/* Builder Name */}
      <Box sx={{ mb: 2 }} id={fieldId("builderName")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Builder Name <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter builder name"
          value={formData.builderName}
          onChange={handleInputChange("builderName")}
          size="small"
          error={!!errors.builderName}
          helperText={errors.builderName}
          sx={outlinedFieldSx}
        />
      </Box>

      {/* Address - User enters manually */}
      <Box sx={{ mb: 2 }} id={fieldId("address")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Address <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter complete address"
          value={formData.address}
          onChange={handleInputChange("address")}
          multiline
          rows={3}
          size="small"
          error={!!errors.address}
          helperText={errors.address}
          sx={outlinedFieldSx}
        />
      </Box>

      {/* City & Landmark */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} id={fieldId("city")}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
          >
            City <span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <FormControl fullWidth size="small" error={!!errors.city}>
            <Select
              value={formData.city}
              onChange={handleInputChange("city")}
              displayEmpty
              sx={outlinedSelectSx}
            >
              <MenuItem value="" disabled>
                Select city
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
            {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
          >
            Landmark
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter landmark"
            value={formData.landmark}
            onChange={handleInputChange("landmark")}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& fieldset": { borderColor: "#E0E0E0" },
                "&:hover fieldset": { borderColor: "#BDBDBD" },
                "&.Mui-focused fieldset": { borderColor: "#1976D2" },
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Property Type */}
      <Box sx={{ mb: 2 }} id={fieldId("propertyType")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Property Type <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <FormControl fullWidth size="small" error={!!errors.propertyType}>
          <Select
            value={formData.propertyType}
            onChange={handlePropertyTypeChange}
            displayEmpty
            sx={outlinedSelectSx}
          >
            <MenuItem value="" disabled>
              Select property type
            </MenuItem>
            {propertyTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          {errors.propertyType && (
            <FormHelperText>{errors.propertyType}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Project Overview: Towers / Floors / Units / Total Area
          - Apartment, Independent House, Villa -> all 4 fields
          - Plot/Land -> Units + Total Area only */}
      {(PROJECT_OVERVIEW_TYPES.includes(formData.propertyType) ||
        formData.propertyType === "Plots/Lands") && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
          >
            Project Overview
          </Typography>
          <Grid container spacing={2}>
            {PROJECT_OVERVIEW_TYPES.includes(formData.propertyType) && (
              <>
                <Grid item xs={6}>
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
                  >
                    Towers
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. 6"
                    type="number"
                    value={formData.towers}
                    onChange={handleInputChange("towers")}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E0E0E0" },
                        "&:hover fieldset": { borderColor: "#BDBDBD" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
                  >
                    Floors
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. 18"
                    type="number"
                    value={formData.overallFloors}
                    onChange={handleInputChange("overallFloors")}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E0E0E0" },
                        "&:hover fieldset": { borderColor: "#BDBDBD" },
                      },
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
              >
                Units
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 780"
                type="number"
                value={formData.units}
                onChange={handleInputChange("units")}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#E0E0E0" },
                    "&:hover fieldset": { borderColor: "#BDBDBD" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
              >
                Total Project Area (sqft)
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 50000"
                type="number"
                value={formData.siteArea}
                onChange={handleInputChange("siteArea")}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "& fieldset": { borderColor: "#E0E0E0" },
                    "&:hover fieldset": { borderColor: "#BDBDBD" },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Possession Status */}
      <Box sx={{ mb: 2 }} id={fieldId("possessionStatus")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Possession Status <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        <FormControl fullWidth size="small" error={!!errors.possessionStatus}>
          <Select
            value={formData.possessionStatus}
            onChange={(e) => {
              const value = e.target.value;
              clearError("possessionStatus");
              if (value !== "Under Construction") clearError("possessionDate");
              setFormData((prev) => ({
                ...prev,
                possessionStatus: value,
                // Clear the date if switching away from Under Construction
                possessionDate: value === "Under Construction" ? prev.possessionDate : "",
              }));
            }}
            displayEmpty
            sx={outlinedSelectSx}
          >
            <MenuItem value="" disabled>
              Select possession status
            </MenuItem>
            {possessionStatusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
          {errors.possessionStatus && (
            <FormHelperText>{errors.possessionStatus}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {/* Possession Date - only shown when Under Construction */}
      {formData.possessionStatus === "Under Construction" && (
        <Box sx={{ mb: 2 }} id={fieldId("possessionDate")}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
          >
            Possession Date <span style={{ color: "#E53935" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.possessionDate}
            onChange={handleInputChange("possessionDate")}
            size="small"
            InputLabelProps={{ shrink: true }}
            error={!!errors.possessionDate}
            helperText={errors.possessionDate}
            sx={outlinedFieldSx}
          />
        </Box>
      )}

      {/* Logo Image */}
      {renderLogoUpload()}

      {/* Location Map - Select location on map */}
      <Box sx={{ mb: 2 }} id={fieldId("location")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
        >
          Select Location on Map <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        
        {/* Location Search */}
        {isLoaded && (
          <GoogleAutocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={onPlaceChanged}
          >
            <TextField
              fullWidth
              placeholder="Search location on map"
              value={locationAddress}
              onChange={(e) => setLocationAddress(e.target.value)}
              size="small"
              error={!!errors.location}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={getCurrentLocation} edge="end" size="small">
                      <MyLocation sx={{ color: "#9a4f20" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                ...outlinedFieldSx,
              }}
            />
          </GoogleAutocomplete>
        )}

        {/* Map */}
        {isLoaded ? (
          <div style={{ ...containerStyle, position: 'relative' }}>
           <GoogleMap
  mapContainerStyle={{ width: '100%', height: '100%' }}
  center={location}
  zoom={15}
  options={{ gestureHandling: 'greedy' }}
  onLoad={(map) => {
    mapRef.current = map;
  }}
  onClick={handleMapClick}
  onDragStart={() => {
    setLocationConfirmed(false);
  }}
/>

            {/* Fixed pin at center */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -100%)',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
                <path
                  fill="#ff0000"
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                />
              </svg>
            </div>

            {/* Confirm location button */}
            <button
              type="button"
              onClick={confirmLocation}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: locationConfirmed ? 'green' : 'red',
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                position: 'absolute',
                right: '10px',
                top: '45%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                transition: 'background-color 0.3s ease',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                <path fill="white" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </button>
          </div>
        ) : (
          <Typography sx={{ textAlign: 'center', py: 4 }}>
            {loadError ? 'Error loading Google Maps. Please check your API key.' : 'Loading Google Maps...'}
          </Typography>
        )}

        {errors.location && (
          <Typography
            variant="caption"
            sx={{ color: "#d32f2f", display: "block", mt: 0.5 }}
          >
            {errors.location}
          </Typography>
        )}
      </Box>

      {/* Hero Images */}
      <Box id={fieldId("heroImages")}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
        >
          Project Images (10 images) <span style={{ color: "#E53935" }}>*</span>
        </Typography>
        {renderImageUpload("hero", heroImages, "Upload Images")}
      </Box>
    </Box>
  );

  // Floor plan uploader for ONE configuration (type = "2D" | "3D")
  // Only ONE image is allowed per type - once uploaded, the "add" tile
  // is hidden; removing the image brings it back.
const renderFloorPlanUploader = (config, type) => {
  const field = getFloorPlanField(type);
  const images = config[field];
  const inputId = `floor-plan-${type}-${config.id}`;
  const hasImage = images.length > 0;

  return (
    <Box sx={{ mb: 2 }} key={inputId}>
      <Typography
        sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 1 }}
      >
        {type} Floor Plan Image
      </Typography>

      {hasImage ? (
        // ---------- Preview state ----------
        <Box
          sx={{
            position: "relative",
            width: "100%",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#FAFAFA",
          }}
        >
          <Box
            component="img"
            src={images[0].url}
            alt={`${type} floor plan`}
            sx={{
              display: "block",
              width: "100%",
              height: "180px",
              objectFit: "contain",
              backgroundColor: "#FFFFFF",
            }}
          />

          {/* Remove button */}
          <IconButton
            size="small"
            onClick={() => removeFloorPlanImage(config.id, type, 0)}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFF",
              padding: "3px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
            }}
          >
            <Close sx={{ fontSize: "16px" }} />
          </IconButton>

          {/* Replace button */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => document.getElementById(inputId).click()}
            sx={{
              position: "absolute",
              bottom: 6,
              right: 6,
              textTransform: "none",
              fontSize: "11px",
              py: 0.3,
              px: 1,
              borderRadius: "6px",
              backgroundColor: "#FFFFFF",
              borderColor: "#9a4f20",
              color: "#9a4f20",
              "&:hover": {
                backgroundColor: "#F5EDE4",
                borderColor: "#9a4f20",
              },
            }}
          >
            Change
          </Button>
        </Box>
      ) : (
        // ---------- Empty upload state (bigger dropzone) ----------
        <Box
          onClick={() => document.getElementById(inputId).click()}
          sx={{
            width: "100%",
            minHeight: "120px",
            border: "2px dashed #D0D0D0",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backgroundColor: "#FAFAFA",
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: "#F0F0F0",
              borderColor: "#9a4f20",
            },
          }}
        >
          <Add sx={{ fontSize: "36px", color: "#999" }} />
          <Typography
            sx={{ fontSize: "12px", color: "#666", mt: 0.5, fontWeight: 500 }}
          >
            Upload {type} Floor Plan
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "#999", mt: 0.3 }}>
            JPG, PNG (single image)
          </Typography>
        </Box>
      )}

      <input
        id={inputId}
        type="file"
        accept="image/*"
        onChange={handleFloorPlanUpload(config.id, type)}
        style={{ display: "none" }}
      />
    </Box>
  );
};

  // Generic 2-column form used for Plot and Commercial configurations
  const renderConfigFields = (config, fields) => {
    const inputSx = {
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        "& fieldset": { borderColor: "#E0E0E0" },
        "&:hover fieldset": { borderColor: "#BDBDBD" },
      },
    };
    const selectSx = {
      borderRadius: "8px",
      backgroundColor: "#FFFFFF",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E0E0E0" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#BDBDBD" },
    };
    const perSqft = propertyKind === "commercial" ? getPricePerSqft(config) : null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {fields.map((f) => (
            <Box
              key={f.field}
              sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
            >
              <Typography
                sx={{ fontSize: "12px", fontWeight: 500, color: "#333", mb: 0.5 }}
              >
                {f.label}
              </Typography>
              {f.type === "select" ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={config[f.field] || ""}
                    onChange={(e) =>
                      handleConfigChange(config.id, f.field, e.target.value)
                    }
                    displayEmpty
                    sx={selectSx}
                  >
                    <MenuItem value="" disabled>
                      {`Select ${f.label.toLowerCase()}`}
                    </MenuItem>
                    {f.options.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  type={f.type === "number" ? "number" : "text"}
                  placeholder={f.placeholder || "Enter"}
                  value={config[f.field] ?? ""}
                  onChange={(e) =>
                    handleConfigChange(config.id, f.field, e.target.value)
                  }
                  inputProps={
                    f.type === "number"
                      ? { min: 0, ...(f.max ? { max: f.max } : {}) }
                      : undefined
                  }
                  sx={inputSx}
                />
              )}
            </Box>
          ))}
        </Box>
        {perSqft && (
          <Typography sx={{ fontSize: "12px", color: "#666", mt: 1 }}>
            Price per sqft (auto): ₹{perSqft.toLocaleString("en-IN")}
          </Typography>
        )}
      </Box>
    );
  };

  // ============================================================
  // PLANS SECTION — cards now side-scroll horizontally
  // ============================================================
  const renderPlansSection = () => (
    <Box>
      {/* Tabs: BHK types / plot directions / commercial spaces */}
      {planTabs.length > 0 && (
        <Box
          sx={{
            mb: 1,
            overflowX: "auto",
            whiteSpace: "nowrap",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Stack direction="row" spacing={1} sx={{ display: "inline-flex" }}>
            {planTabs.map((tab) => {
              // How many filled configs this tab already has
              const filledCount = formData.configurations.filter(
                (c) => c.unitType === tab && isConfigFilled(c),
              ).length;
              const tabText = getTabLabel(tab);

              return (
                <Chip
                  key={tab}
                  label={filledCount > 0 ? `${tabText} (${filledCount})` : tabText}
                  onClick={() => handleTabChange(tab)}
                  variant={selectedTab === tab ? "filled" : "outlined"}
                  sx={{
                    borderRadius: "10px",
                    px: 1,
                    minWidth: "60px",
                    "& .MuiChip-label": {
                      fontWeight: 500,
                      color: selectedTab === tab ? "#FFFFFF" : "#333333",
                    },
                    ...(selectedTab === tab && {
                      backgroundColor: "#9a4f20",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#7a3f1a",
                      },
                    }),
                    ...(selectedTab !== tab && {
                      backgroundColor: "transparent",
                      borderColor: "#D0D0D0",
                      "&:hover": {
                        backgroundColor: "#F5F5F5",
                      },
                    }),
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      )}
      <Typography
        sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 2 }}
      >
        {plansHeading}
      </Typography>

      {/* ✅ Side-scrollable row of configuration cards */}
      <Box
        ref={configsRowRef}
        sx={{
          display: "flex",
          gap: 1.5,
          // Only allow horizontal scrolling when there are 2+ cards
          overflowX: visibleConfigs.length > 1 ? "auto" : "hidden",
          overflowY: "hidden",
          scrollSnapType: visibleConfigs.length > 1 ? "x mandatory" : "none",
          WebkitOverflowScrolling: "touch",
          pb: visibleConfigs.length > 1 ? 1 : 0,
          mb: 1,
          px: 0.25,
          ...(visibleConfigs.length > 1 && {
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D0D0D0",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }),
        }}
      >
        {visibleConfigs.map((config, index) => {
          const isSingle = visibleConfigs.length === 1;

          return (
            <Card
              key={config.id}
              sx={{
                // 👇 100% when alone, 85% (with min/max clamp) when sharing the row
                flex: isSingle ? "1 1 100%" : "0 0 85%",
                minWidth: isSingle ? 0 : 260,
                maxWidth: isSingle ? "100%" : 360,
                scrollSnapAlign: "start",
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                boxShadow: "none",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent sx={{ p: 1, flexGrow: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {propertyKind === "plot"
                      ? `${config.unitType} Plot #${index + 1}`
                      : `${getTabLabel(config.unitType)} #${index + 1}`}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => toggleConfig(config.id)}
                    >
                      {config.isOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => removeConfiguration(config.id)}
                      sx={{ color: "#E53935" }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={config.isOpen}>
                  {propertyKind === "bhk" ? (
                    <>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1, alignItems: "flex-start" }}>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
                      id={fieldId(`cfg-${config.id}-carpetArea`)}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Carpet Area (sqft) <span style={{ color: "#E53935" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter area"
                        value={config.carpetArea}
                        onChange={(e) =>
                          handleConfigChange(config.id, "carpetArea", e.target.value)
                        }
                        size="small"
                        error={!!errors[`cfg-${config.id}-carpetArea`]}
                        helperText={errors[`cfg-${config.id}-carpetArea`]}
                        sx={outlinedFieldSx}
                      />
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
                      id={fieldId(`cfg-${config.id}-builtUpArea`)}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Built-up Area (sqft) <span style={{ color: "#E53935" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter area"
                        value={config.builtUpArea}
                        onChange={(e) =>
                          handleConfigChange(config.id, "builtUpArea", e.target.value)
                        }
                        size="small"
                        error={!!errors[`cfg-${config.id}-builtUpArea`]}
                        helperText={errors[`cfg-${config.id}-builtUpArea`]}
                        sx={outlinedFieldSx}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                    <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Super Built-up (sqft)
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter area"
                        value={config.superBuiltUp}
                        onChange={(e) =>
                          handleConfigChange(config.id, "superBuiltUp", e.target.value)
                        }
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&:hover fieldset": { borderColor: "#BDBDBD" },
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
                      id={fieldId(`cfg-${config.id}-price`)}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Price <span style={{ color: "#E53935" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter price"
                        value={config.price}
                        onChange={(e) =>
                          handleConfigChange(config.id, "price", e.target.value)
                        }
                        size="small"
                        type="number"
                        error={!!errors[`cfg-${config.id}-price`]}
                        helperText={errors[`cfg-${config.id}-price`]}
                        sx={outlinedFieldSx}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5, alignItems: "flex-start" }}>
                    <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Facing
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={config.facing}
                          onChange={(e) =>
                            handleConfigChange(config.id, "facing", e.target.value)
                          }
                          displayEmpty
                          sx={{
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#BDBDBD",
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select facing
                          </MenuItem>
                          {facingOptions.map((facing) => (
                            <MenuItem key={facing} value={facing}>
                              {facing}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
                      id={fieldId(`cfg-${config.id}-bathrooms`)}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Bathrooms <span style={{ color: "#E53935" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter"
                        value={config.bathrooms || ""}
                        onChange={(e) =>
                          handleConfigChange(config.id, "bathrooms", e.target.value)
                        }
                        size="small"
                        type="number"
                        error={!!errors[`cfg-${config.id}-bathrooms`]}
                        helperText={errors[`cfg-${config.id}-bathrooms`]}
                        sx={outlinedFieldSx}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "flex-start" }}>
                    <Box
                      sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}
                      id={fieldId(`cfg-${config.id}-balcony`)}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Balcony <span style={{ color: "#E53935" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter"
                        value={config.balcony}
                        onChange={(e) =>
                          handleConfigChange(config.id, "balcony", e.target.value)
                        }
                        size="small"
                        error={!!errors[`cfg-${config.id}-balcony`]}
                        helperText={errors[`cfg-${config.id}-balcony`]}
                        sx={outlinedFieldSx}
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 calc(50% - 6px)", minWidth: "120px" }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#333",
                          mb: 0.5,
                        }}
                      >
                        Parking
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter"
                        value={config.parking || ""}
                        onChange={(e) =>
                          handleConfigChange(config.id, "parking", e.target.value)
                        }
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#FFFFFF",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&:hover fieldset": { borderColor: "#BDBDBD" },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                    </>
                  ) : (
                    renderConfigFields(
                      config,
                      propertyKind === "plot" ? plotFields : commercialFields,
                    )
                  )}

                  {/* Floor plans: available for all property kinds (BHK, Plot, Commercial) */}
                  {renderFloorPlanUploader(config, "2D")}
                  {renderFloorPlanUploader(config, "3D")}
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Small hint + count */}
      {visibleConfigs.length > 1 && (
        <Typography
          sx={{
            fontSize: "11px",
            color: "#888",
            mt: 0.5,
            mb: 1,
            textAlign: "center",
          }}
        >
          {visibleConfigs.length} configuration(s) · swipe / scroll → to see more
        </Typography>
      )}

      <Button
        fullWidth
        variant="outlined"
        startIcon={<Add />}
        onClick={addConfiguration}
        sx={{
          borderRadius: "8px",
          py: 1.5,
          borderColor: "#9a4f20",
          color: "#9a4f20",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": {
            borderColor: "#9a4f20",
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
        }}
      >
        {`Add Another ${getTabLabel(selectedTab)}${propertyKind === "plot" ? " Plot" : ""}`}
      </Button>
    </Box>
  );

  // ============================================================
  // FEATURES SECTION (highlights + amenities) - uses unique keys
  // ============================================================
  const renderFeaturesSection = () => (
    <Box>
      <Typography
        sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 2 }}
      >
        Property Highlights
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "8px",
          mb: 4,
        }}
      >
        {highlightOptions.map((item) => {
          const itemKey = getHighlightKey(item);
          const isSelected = formData.highlights.includes(itemKey);
          return (
            <Box
              key={itemKey}
              onClick={() => toggleHighlight(itemKey)}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 0.5,
                p: 1,
                borderRadius: "10px",
                border: `1px solid ${isSelected ? "#9a4f20" : "#E0E0E0"}`,
                backgroundColor: isSelected ? "#F5EDE4" : "#FFFFFF",
                cursor: "pointer",
                transition: "all 0.2s",
                minHeight: "90px",
                justifyContent: "center",
                overflow: "hidden",
                "&:hover": {
                  backgroundColor: isSelected ? "#EDE0D4" : "#FAFAFA",
                  borderColor: isSelected ? "#9a4f20" : "#B0B0B0",
                },
              }}
            >
              {isSelected && (
                <CheckCircle
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    fontSize: "14px",
                    color: "#9a4f20",
                  }}
                />
              )}
              <Box
                sx={{
                  color: "#9a4f20",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  mb: 0.5,
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 600,
                  color: "#1A1A1A",
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {item.label}
              </Typography>
              {item.subtitle && (
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 400,
                    color: "#888",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {item.subtitle}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography
        sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 1.5 }}
      >
        Amenities
      </Typography>

      {/* Category Tabs */}
      <Box
        sx={{
          mb: 2,
          overflowX: "auto",
          whiteSpace: "nowrap",
          borderBottom: "1px solid #E0E0E0",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack direction="row" spacing={0} sx={{ display: "inline-flex" }}>
          {amenityCategories.map((cat) => (
            <Box
              key={cat}
              onClick={() => setSelectedAmenityCategory(cat)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: "pointer",
                borderBottom:
                  selectedAmenityCategory === cat
                    ? "2px solid #9a4f20"
                    : "2px solid transparent",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: selectedAmenityCategory === cat ? 600 : 500,
                  color: selectedAmenityCategory === cat ? "#9a4f20" : "#666",
                  whiteSpace: "nowrap",
                }}
              >
                {cat}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Amenities Grid - grouped by category when "All", flat otherwise */}
      {(selectedAmenityCategory === "All"
        ? amenityCategories.filter((c) => c !== "All")
        : [selectedAmenityCategory]
      ).map((cat) => {
        const items = amenitiesOptions.filter((a) => a.category === cat);
        if (items.length === 0) return null;

        return (
          <Box key={cat} sx={{ mb: 3 }}>
            <Typography
              sx={{ fontSize: "13px", fontWeight: 700, color: "#333", mb: 1.5 }}
            >
              {cat}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "8px",
              }}
            >
              {items.map((item) => {
                const itemKey = getAmenityKey(item);
                const isSelected = formData.amenities.includes(itemKey);
                return (
                  <Box
                    key={itemKey}
                    onClick={() => toggleAmenity(itemKey)}
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      gap: 0.5,
                      p: 1,
                      borderRadius: "10px",
                      border: `1px solid ${isSelected ? "#9a4f20" : "#E0E0E0"}`,
                      backgroundColor: isSelected ? "#F5EDE4" : "#FFFFFF",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      minHeight: "74px",
                      justifyContent: "center",
                      overflow: "hidden",
                      "&:hover": {
                        backgroundColor: isSelected ? "#EDE0D4" : "#FAFAFA",
                        borderColor: isSelected ? "#9a4f20" : "#B0B0B0",
                      },
                    }}
                  >
                    {isSelected && (
                      <CheckCircle
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          fontSize: "13px",
                          color: "#9a4f20",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        color: "#9a4f20",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "9.5px",
                        fontWeight: 600,
                        color: "#1A1A1A",
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

// Render Specifications section
const renderSpecificationsSection = () => (
  <Box>
    <Typography
      sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 1 }}
    >
      Specifications
    </Typography>

    <TextField
      fullWidth
      placeholder="Enter amenities (e.g. Swimming Pool, Gym, Kids Pool)"
      value={formData.amenitiesText || ""}
      onChange={(e) => {
        setFormData((prev) => ({
          ...prev,
          amenitiesText: e.target.value,
        }));
      }}
      size="small"
      sx={{
        mb: 3,
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          "& fieldset": { borderColor: "#E0E0E0" },
          "&:hover fieldset": { borderColor: "#BDBDBD" },
          "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
          "& input": {
            padding: "10px 14px",
            fontSize: "13px",
          },
        },
      }}
    />

    <Typography
      sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 2 }}
    >
      Specifications (Key-Value pairs)
    </Typography>

    {specificationFields.map((field) => (
      <Box key={field.key} sx={{ mb: 2 }}>
        {/* Label Row - Icon + Label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              color: "#9a4f20",
              display: "flex",
              alignItems: "center",
              fontSize: "18px",
            }}
          >
            {field.icon}
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#333",
            }}
          >
            {field.label}
          </Typography>
        </Box>

        {/* Input Row - Below the label */}
        <TextField
          fullWidth
          placeholder="Enter details"
          value={formData.specifications[field.key] || ""}
          onChange={handleSpecificationChange(field.key)}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
              "& input": {
                padding: "10px 14px",
                fontSize: "13px",
              },
            },
          }}
        />
      </Box>
    ))}

    {formData.customSpecifications.map((item) => (
      <Paper
        key={item.id}
        sx={{ p: 2, mb: 2, border: "1px solid #E0E0E0", borderRadius: "8px" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <TextField
              fullWidth
              placeholder="Key"
              value={item.key}
              onChange={(e) =>
                updateCustomSpecification(item.id, "key", e.target.value)
              }
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "& fieldset": { borderColor: "#E0E0E0" },
                  "&:hover fieldset": { borderColor: "#BDBDBD" },
                },
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              fullWidth
              placeholder="Value"
              value={item.value}
              onChange={(e) =>
                updateCustomSpecification(item.id, "value", e.target.value)
              }
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "& fieldset": { borderColor: "#E0E0E0" },
                  "&:hover fieldset": { borderColor: "#BDBDBD" },
                },
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              onClick={() => removeCustomSpecification(item.id)}
              sx={{
                color: "#E53935",
                padding: "4px",
              }}
            >
              <DeleteOutline />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>
    ))}

    <Button
      fullWidth
      variant="outlined"
      startIcon={<Add />}
      onClick={addCustomSpecification}
      sx={{
        borderRadius: "8px",
        py: 1.5,
        borderColor: "#9a4f20",
        color: "#9a4f20",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
          borderColor: "#9a4f20",
          backgroundColor: "rgba(154, 79, 32, 0.04)",
        },
      }}
    >
      Add Another Specification
    </Button>

    <Typography
      sx={{
        fontSize: "12px",
        color: "#111010",
        mt: 2,
        textAlign: "center",
      }}
    >
      Specifications should be like project builder form format
    </Typography>
  </Box>
);

  // Render Location section
  const renderLocationSection = () => (
    <Box>
      {formData.locationAdvantages.map((place, index) => (
        <Card
          key={place.id}
          sx={{
            mb: 1,
            borderRadius: "8px",
            border: "1px solid #E0E0E0",
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
              >
                Place #{index + 1}
              </Typography>
              {formData.locationAdvantages.length > 1 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => removeLocationAdvantage(place.id)}
                  startIcon={<DeleteOutline />}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontSize: "12px",
                  }}
                >
                  Remove
                </Button>
              )}
            </Box>

            <Box sx={{ mb: 2 }} id={fieldId(`loc-${place.id}-name`)}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#333",
                  mb: 0.5,
                }}
              >
                Place Name <span style={{ color: "#E53935" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter place name"
                value={place.name}
                onChange={(e) =>
                  updateLocationAdvantage(place.id, "name", e.target.value)
                }
                size="small"
                error={!!errors[`loc-${place.id}-name`]}
                helperText={errors[`loc-${place.id}-name`]}
                sx={outlinedFieldSx}
              />
            </Box>
            <Box sx={{ mb: 2 }} id={fieldId(`loc-${place.id}-distance`)}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#333",
                  mb: 0.5,
                }}
              >
                Distance <span style={{ color: "#E53935" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter distance (e.g. 2.5 km)"
                value={place.distance}
                onChange={(e) =>
                  updateLocationAdvantage(place.id, "distance", e.target.value)
                }
                size="small"
                error={!!errors[`loc-${place.id}-distance`]}
                helperText={errors[`loc-${place.id}-distance`]}
                sx={outlinedFieldSx}
              />
            </Box>
            <Box sx={{ mb: 2 }} id={fieldId(`loc-${place.id}-travelTime`)}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#333",
                  mb: 0.5,
                }}
              >
                Travel Time <span style={{ color: "#E53935" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter travel time (e.g. 10 min)"
                value={place.travelTime}
                onChange={(e) =>
                  updateLocationAdvantage(
                    place.id,
                    "travelTime",
                    e.target.value,
                  )
                }
                size="small"
                error={!!errors[`loc-${place.id}-travelTime`]}
                helperText={errors[`loc-${place.id}-travelTime`]}
                sx={outlinedFieldSx}
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      {formData.locationAdvantages.length < 8 && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Add />}
          onClick={addLocationAdvantage}
          sx={{
            borderRadius: "8px",
            py: 1,
            borderColor: "#9a4f20",
            color: "#9a4f20",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#9a4f20",
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Add Another Place
        </Button>
      )}
    </Box>
  );

  // Render About section
  const renderAboutSection = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Description
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter project description"
          value={formData.aboutDescription}
          onChange={handleInputChange("aboutDescription")}
          multiline
          rows={4}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#1976D2" },
            },
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#999", mt: 0.5 }}>
          0 / 2000
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography
        sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A", mb: 2 }}
      >
        Builder Details
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Builder Name
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter builder name"
          value={formData.builderDetails.name}
          onChange={handleBuilderChange("name")}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
            },
          }}
        />
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Typography
            sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
          >
            City
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={formData.builderDetails.city}
              onChange={handleBuilderChange("city")}
              displayEmpty
              sx={{
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#BDBDBD",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9a4f20",
                },
              }}
            >
              <MenuItem value="" disabled>
                Select city
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Typography
            sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
          >
            Projects Completed
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter number"
            value={formData.builderDetails.projectsCompleted}
            onChange={handleBuilderChange("projectsCompleted")}
            size="small"
            type="number"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& fieldset": { borderColor: "#E0E0E0" },
                "&:hover fieldset": { borderColor: "#BDBDBD" },
                "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
              },
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 500, color: "#333", mb: 0.5 }}
        >
          Ongoing Projects
        </Typography>
        <TextField
          fullWidth
          placeholder="Enter number"
          value={formData.builderDetails.ongoingProjects}
          onChange={handleBuilderChange("ongoingProjects")}
          size="small"
          type="number"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&:hover fieldset": { borderColor: "#BDBDBD" },
              "&.Mui-focused fieldset": { borderColor: "#9a4f20" },
            },
          }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography
  sx={{
    fontSize: "16px",
    fontWeight: 600,
    color: "#1A1A1A",
    mb: 2,
  }}
>
  Brochure
</Typography>

<Box sx={{ mb: 3 }}>
  <Typography
    sx={{
      fontSize: "14px",
      fontWeight: 500,
      color: "#333",
      mb: 1,
    }}
  >
    Upload Brochure
  </Typography>

  <Box
    sx={{
      border: "2px dashed #D0D0D0",
      borderRadius: "8px",
      p: 3,
      textAlign: "center",
      bgcolor: "#FFFFFF",
      cursor: formData.brochureFiles.length >= 1 ? "not-allowed" : "pointer",
      opacity: formData.brochureFiles.length >= 1 ? 0.6 : 1,
      "&:hover": formData.brochureFiles.length >= 1
        ? {}
        : {
            bgcolor: "#F0F0F0",
            borderColor: "#B0B0B0",
          },
    }}
    onClick={() => {
      if (formData.brochureFiles.length < 1) {
        document.getElementById("brochure-upload").click();
      }
    }}
  >
    <CloudUpload sx={{ fontSize: "40px", color: "#999" }} />

    <Typography
      sx={{
        fontSize: "13px",
        color: "#666",
        mt: 1,
      }}
    >
      PDF up to 50MB
    </Typography>

    <input
      id="brochure-upload"
      type="file"
      accept=".pdf"
      multiple={false}
      onChange={handleBrochureUpload}
      style={{ display: "none" }}
      disabled={formData.brochureFiles.length >= 1}
    />
  </Box>

  {/* Small helper text */}
  <Typography
    sx={{
      fontSize: "11px",
      color: "#888",
      mt: 0.5,
    }}
  >
    You can upload only one PDF file.
  </Typography>

  {formData.brochureFiles.length > 0 && (
    <Box sx={{ mt: 1 }}>
      {formData.brochureFiles.map((file, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            bgcolor: "#F5F5F5",
            borderRadius: "4px",
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              color: "#333",
            }}
          >
            {file.name}
          </Typography>

          <IconButton
            size="small"
            onClick={() => removeBrochure(index)}
          >
            <Close sx={{ fontSize: "14px" }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  )}
</Box>
    </Box>
  );

  // Show loading or error state
  if (!isLoaded && !loadError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading Google Maps...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: "#fff",
          boxShadow: 1,
        }}
      >
        <SearchBar onBackClick={() => navigate(-1)} />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          pt: { xs: "60px", sm: "100px" },
          backgroundColor: "#F5F5F5",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 3 },
            pb: 10,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, sm: 3 },
              mb: 3,
              width: "100%",
              maxWidth: "600px",
              boxSizing: "border-box",
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
            }}
          >
            {/* Header */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <IconButton onClick={() => navigate(-1)} sx={{ p: 0 }}>
                <ArrowBack sx={{ color: "#000" }} />
              </IconButton>
              <Typography
                sx={{ fontSize: "20px", fontWeight: 700, color: "#1A1A1A" }}
              >
                Add New Property
              </Typography>
            </Box>

            {/* All Sections */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Section 1: Details */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  1. Project & Builder Details
                </Typography>
                {renderDetailsSection()}
              </Box>

              {/* Section 2: Plans */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  2. Plans & Configurations
                </Typography>
                {renderPlansSection()}
              </Box>

              {/* Section 3: Features */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  3. Features & Amenities
                </Typography>
                {renderFeaturesSection()}
              </Box>

              {/* Section 4: Specifications */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  4. Specifications
                </Typography>
                {renderSpecificationsSection()}
              </Box>

              {/* Section 5: Location */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  5. Location Advantages (up to 8)
                </Typography>
                {renderLocationSection()}
              </Box>

              {/* Section 6: About */}
              <Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ mb: 1, fontWeight: 600, color: "#1A1A1A" }}
                >
                  6. About Project
                </Typography>
                {renderAboutSection()}
              </Box>

              {/* Submit Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: "8px",
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    borderColor: "#9a4f20",
                    color: "#9a4f20",
                    "&:hover": {
                      borderColor: "#9a4f20",
                      backgroundColor: "rgba(154, 79, 32, 0.04)",
                    },
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: "8px",
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    backgroundColor: "#9a4f20",
                    "&:hover": { backgroundColor: "#7a3f1a" },
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box sx={{ height: "70px" }} />

      <FullScreenLoader open={loading} text="Adding property..." />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowSuccess(false);
            navigate("/post");
          }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            setShowFailure(false);
            navigate("/post");
          }}
        />
      )}

      <FormsBottomNavbar />
    </>
  );
};

export default AddNewProperty;